/** The eagle cry, and the one rule that decides when it can actually play.
 *
 *  BROWSERS DO NOT LET A PAGE MAKE NOISE ON ITS OWN. Chrome, Safari and Firefox
 *  all refuse audio until the person has interacted with the page -- a tap, a
 *  click, a key. This is not a setting anyone can turn off; it is the policy
 *  that stopped the web being full of pages that shout at you. So "play when
 *  the app opens" is only possible at the moment the opening involves a tap.
 *
 *  In practice that is signing in, which is a real moment and arguably the
 *  right one: it is the app saying you are in. Opening an already-signed-in
 *  app from the home screen has no tap in it, so the attempt is made and
 *  usually refused. That refusal is caught and dropped -- a browser declining
 *  to play a sound is normal, not an error, and it must never reach the person
 *  holding the phone or the console.
 *
 *  MUTED IS A FIRST-CLASS ANSWER. A crew member opens this app several times a
 *  day, five days a week. A sound that is a nice touch on Monday is an
 *  annoyance by Thursday and a reason to leave the phone on silent by the
 *  following week -- which would cost them the notification sounds that
 *  matter. So it is one tap to turn off, per device, remembered.
 */
const SRC = "/sounds/eagle-cry.mp3";
const MUTE_KEY = "talon.sound.muted";

let el = null;

/** Muted unless we can prove otherwise: a browser with storage blocked (a
 *  private window, a locked-down device) should be quiet rather than loud,
 *  because the person there has no way to make it stop. */
export function isMuted() {
    try {
        return localStorage.getItem(MUTE_KEY) === "1";
    } catch (e) {
        return true;
    }
}

export function setMuted(muted) {
    try {
        localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
    } catch (e) {
        /* storage unavailable -- the toggle still works for this page view */
    }
}

/** Play the cry. Never throws, never logs, never blocks anything.
 *
 *  Returns a promise that resolves true if it actually played. Nothing in the
 *  app waits on it; the return value is there so the browser checks can assert
 *  on it rather than guessing from a timer.
 */
export function playEagleCry() {
    if (isMuted()) return Promise.resolve(false);
    try {
        if (!el) {
            el = new Audio(SRC);
            el.preload = "auto";
            // Well under the notification sounds it sits beside. A startup
            // chime that is louder than a message is a startup chime people
            // turn off.
            el.volume = 0.45;
        }
        el.currentTime = 0;
        const p = el.play();
        if (!p || typeof p.then !== "function") return Promise.resolve(true);
        return p.then(() => true).catch(() => false);
    } catch (e) {
        return Promise.resolve(false);
    }
}
